import { DataHighlight, InfoTooltip, SkeletonInput, Text } from "@wayflyer/flyui";
import { IconArrowDown16Line, IconArrowUp16Line } from "@wayflyer/flyui-icons/16/line";
import type { CSSProperties } from "react";
import { styled } from "styled-components";

import { MetricReferenceDirection } from "../shared";
import { getReferenceColor } from "../styled";

export interface MetricCardProps {
  title: string | UIElement;
  metricValue?: string;
  metricReferenceValue?: string;
  metricReferenceDirection?: MetricReferenceDirection;
  metricReferenceSuccess?: boolean;
  metricReferenceText?: string;
  tooltip?: string;
  loading?: boolean;
  style?: CSSProperties;
  height?: number;
}

const Container = styled.div``;

const LoadingSkeleton = styled(SkeletonInput)`
  align-self: flex-start;
  width: ${(props) => (props.small ? "70%" : "100%")};
  height: ${(props) => (props.small ? "32px" : "22px")};
  margin-top: ${(props) => (props.small ? "8px" : "0px")};
  margin-bottom: ${(props) => (props.small ? "8px" : "0px")};
`;

const MetricContentWrapper = styled.div<{ height: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 225px;
  height: ${(props) => props.height}px;
  padding: 16px;
  box-sizing: content-box;
`;

const MetricLabelText = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const MetricLabel = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const MetricValue = styled.div`
  display: flex;
  width: 100%;
  margin: 0;
  padding-bottom: 4px;
`;

const MetricInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const PercentageWrapper = styled.div<{ direction: MetricReferenceDirection; success: boolean }>`
  display: flex;
  padding: ${({ direction }) => (direction !== MetricReferenceDirection.SAME ? "4px" : "0px")};
  margin-right: 8px;
  border-radius: 4px;
  background-color: ${({ success }) =>
    success ? "var(--palette-bg-status-positive)" : "var(--palette-bg-status-critical)"};
`;

const ReferenceWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 28px;
`;

const MetricReferenceValueText = styled.span<{ success?: boolean; getReferenceColor }>`
  display: flex;
  flex-direction: row;
  font-size: 16px;
  color: ${({ success, getReferenceColor }) =>
    success !== undefined ? getReferenceColor : "var(--palette-content-neutral-strong)"};
  margin: 3px 0 0 0;
`;

const Card = styled.div`
  box-shadow: 0px 1px 2px 0px #0000000f, 0px 1px 3px 0px #0000001a;
  background-color: var(--palette-bg-neutral-base);
  border-radius: var(--sizes-radius-md);
  box-sizing: border-box;
  margin-bottom: 18px;
  width: 100%;
`;

/**
 * MetricCard component
 */
export const MetricCard = ({
  title,
  metricValue = "",
  metricReferenceValue = "",
  metricReferenceDirection,
  metricReferenceSuccess = true,
  loading = false,
  tooltip = "",
  metricReferenceText = "",
  height = 110,
  style,
}: MetricCardProps): UIElement => {
  const showRefText = metricReferenceValue && metricReferenceText;

  const loadingSkeleton = (
    <MetricInfo>
      <LoadingSkeleton small={false} />
      <LoadingSkeleton small={true} />
      <LoadingSkeleton small={false} />
    </MetricInfo>
  );

  return (
    <Container data-testid={`pinned-metric-${title}`}>
      <Card style={style}>
        <MetricContentWrapper height={height} className="metric-content-wrapper">
          {loading ? (
            loadingSkeleton
          ) : (
            <MetricInfo>
              <MetricLabel>
                <MetricLabelText>{typeof title === "string" ? <Text>{title}</Text> : title}</MetricLabelText>
                {tooltip ? <InfoTooltip iconSize="large" content={tooltip} /> : null}
              </MetricLabel>
              <MetricValue>
                <DataHighlight>{metricValue}</DataHighlight>
              </MetricValue>
              <ReferenceWrapper>
                {metricReferenceDirection ? (
                  metricReferenceDirection !== MetricReferenceDirection.SAME ? (
                    <PercentageWrapper
                      direction={metricReferenceDirection}
                      success={metricReferenceSuccess}
                      data-testid="percentage-wrapper"
                    >
                      <MetricReferenceValueText success={metricReferenceSuccess} getReferenceColor={getReferenceColor}>
                        {metricReferenceDirection === MetricReferenceDirection.UP ? (
                          <IconArrowUp16Line data-testid="up-arrow" />
                        ) : metricReferenceDirection === MetricReferenceDirection.DOWN ? (
                          <IconArrowDown16Line data-testid="down-arrow" />
                        ) : null}
                      </MetricReferenceValueText>
                      {/* <SuccessText variant="H4" text={metricReferenceValue} success={metricReferenceSuccess} /> */}
                      {metricReferenceSuccess ? (
                        <Text color="positive">{metricReferenceValue}</Text>
                      ) : (
                        <Text color="critical">{metricReferenceValue}</Text>
                      )}
                    </PercentageWrapper>
                  ) : (
                    <Text>Same {metricReferenceText}</Text>
                  )
                ) : null}
                {showRefText && metricReferenceDirection !== MetricReferenceDirection.SAME ? (
                  <Text>{metricReferenceText}</Text>
                ) : null}
              </ReferenceWrapper>
            </MetricInfo>
          )}
        </MetricContentWrapper>
      </Card>
    </Container>
  );
};
